import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, Box, CircularProgress, Alert
} from '@mui/material';
import { startOfWeek, endOfWeek, addDays, format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { getWeeklySchedule } from '../../collections/workSchedules';
import WeekNavigator from './WeekNavigator';
import ScheduleCell from './ScheduleCell';
import DutyTypeLegend from './DutyTypeLegend';
import ScheduleEntryForm from './ScheduleEntryForm';

const SHIFTS = ['MORNING', 'AFTERNOON'];
const SHIFT_LABELS = { MORNING: 'Sáng', AFTERNOON: 'Chiều' };

/**
 * WorkScheduleCalendar - main calendar component for viewing staff schedules
 * @param {string} controlRole - user role ('ADMIN' or 'STAFF')
 * @param {function} setPageTitle - callback to set page title
 */
const WorkScheduleCalendar = ({ controlRole, setPageTitle }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekEnd = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);

  useEffect(() => {
    if (setPageTitle) {
      setPageTitle('Lịch Làm Việc');
    }
  }, [setPageTitle]);

  const loadScheduleData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getWeeklySchedule(
        format(weekStart, 'yyyy-MM-dd'),
        format(weekEnd, 'yyyy-MM-dd')
      );
      setScheduleData(data);
    } catch (err) {
      console.error('Error loading schedule:', err);
      setError('Không thể tải lịch làm việc. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd]);

  useEffect(() => {
    loadScheduleData();
  }, [loadScheduleData]);

  const handleCellClick = (accountId, accountName, date, shift, existingSchedule) => {
    if (controlRole !== 'ADMIN') return;
    setSelectedCell({
      accountId,
      accountName,
      date,
      shift,
      schedule: existingSchedule
    });
    setFormOpen(true);
  };

  const handleFormClose = (saved) => {
    setFormOpen(false);
    setSelectedCell(null);
    if (saved) {
      loadScheduleData();
    }
  };

  // Generate week days array
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Generate column headers (day + shift combinations)
  const columns = weekDays.flatMap(day =>
    SHIFTS.map(shift => ({
      date: day,
      shift,
      dayLabel: format(day, 'EEEEEE', { locale: vi }),
      shiftLabel: SHIFT_LABELS[shift],
      dateLabel: format(day, 'dd/MM')
    }))
  );

  // Check admin access
  if (controlRole !== 'ADMIN') {
    return (
      <Container maxWidth="xl">
        <Typography component="h1" variant="h5" sx={{ textAlign: "center", mt: 5 }}>
          Bạn không đủ thẩm quyền để truy cập vào tính năng này
        </Typography>
      </Container>
    );
  }

  // Loading state
  if (loading && !scheduleData) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Box mb={3}>
        <Typography variant="h4">Lịch Làm Việc</Typography>
      </Box>

      <WeekNavigator
        currentDate={currentDate}
        onDateChange={setCurrentDate}
        weekStart={weekStart}
        weekEnd={weekEnd}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  minWidth: 150,
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'background.paper',
                  zIndex: 3
                }}
              >
                Nhân sự
              </TableCell>
              {columns.map((col, idx) => (
                <TableCell
                  key={idx}
                  align="center"
                  sx={{
                    minWidth: 80,
                    backgroundColor: 'background.paper'
                  }}
                >
                  <Box>
                    <Typography variant="caption" display="block" fontWeight={500}>
                      {col.dayLabel} {col.shiftLabel}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {col.dateLabel}
                    </Typography>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {scheduleData?.staff_schedules?.map(staff => (
              <TableRow key={staff.account_id} hover>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 1
                  }}
                >
                  {staff.account_name}
                </TableCell>
                {columns.map((col, idx) => {
                  const dateStr = format(col.date, 'yyyy-MM-dd');
                  const daySchedule = staff.schedules?.find(s => s.date === dateStr);
                  const shiftData = daySchedule?.[col.shift.toLowerCase()];

                  return (
                    <ScheduleCell
                      key={idx}
                      schedule={shiftData}
                      disabled={controlRole !== 'ADMIN'}
                      onClick={() => handleCellClick(
                        staff.account_id,
                        staff.account_name,
                        dateStr,
                        col.shift,
                        shiftData
                      )}
                    />
                  );
                })}
              </TableRow>
            ))}
            {(!scheduleData?.staff_schedules || scheduleData.staff_schedules.length === 0) && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Không có dữ liệu nhân sự
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DutyTypeLegend dutyTypes={scheduleData?.duty_types || []} />

      <ScheduleEntryForm
        open={formOpen}
        onClose={handleFormClose}
        cellData={selectedCell}
        dutyTypes={scheduleData?.duty_types || []}
      />
    </Container>
  );
};

export default WorkScheduleCalendar;
